import { MessageIcon } from '@wonderschool/common-base-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Sender = 'me' | 'beale';

const MESSAGES: { sender: Sender; key: string }[] = [
  { sender: 'me', key: 'dashboard.bealeTextingCard.demo.rateRequest' },
  { sender: 'beale', key: 'dashboard.bealeTextingCard.demo.rateConfirm' },
  { sender: 'me', key: 'dashboard.bealeTextingCard.demo.yes' },
  { sender: 'beale', key: 'dashboard.bealeTextingCard.demo.done' },
];

type Frame = { visible: number; typing: boolean };

// Each Beale reply is preceded by about a second of typing dots.
const TIMELINE: (Frame & { at: number })[] = [
  { at: 300, visible: 1, typing: false },
  { at: 900, visible: 1, typing: true },
  { at: 1900, visible: 2, typing: false },
  { at: 2700, visible: 3, typing: false },
  { at: 3300, visible: 3, typing: true },
  { at: 4300, visible: 4, typing: false },
];

const FINAL_FRAME: Frame = { visible: MESSAGES.length, typing: false };

type BealeChatDemoProps = {
  /** Bump to replay the conversation from the start. */
  playKey: number;
  /** Skip the animation and show the finished conversation. */
  reducedMotion: boolean;
};

export default function BealeChatDemo({ playKey, reducedMotion }: BealeChatDemoProps) {
  const { t } = useTranslation();
  const [frame, setFrame] = useState<Frame>(reducedMotion ? FINAL_FRAME : { visible: 0, typing: false });

  useEffect(() => {
    if (reducedMotion) {
      setFrame(FINAL_FRAME);
      return;
    }
    setFrame({ visible: 0, typing: false });
    const timers = TIMELINE.map(({ at, visible, typing }) => setTimeout(() => setFrame({ visible, typing }), at));
    return () => timers.forEach(clearTimeout);
  }, [playKey, reducedMotion]);

  return (
    <div
      className="flex w-64 flex-none flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
      data-testid="beale-chat-demo"
    >
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-900/10">
          <MessageIcon className="h-3.5 w-3.5 text-blue-900" />
        </span>
        <span className="text-sm font-semibold text-navy-950">{t('dashboard.bealeTextingCard.demo.panelTitle')}</span>
      </div>
      <ol
        className="flex h-60 flex-col justify-end gap-2 px-3 py-3"
        aria-label={t('dashboard.bealeTextingCard.demo.label')}
      >
        {MESSAGES.slice(0, frame.visible).map(({ sender, key }) => (
          <li
            key={key}
            data-testid={`beale-chat-message-${sender}`}
            className={
              sender === 'me'
                ? 'max-w-[85%] self-end rounded-2xl rounded-br-sm bg-blue-600 px-3 py-2 text-xs text-white'
                : 'max-w-[85%] self-start rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-xs text-gray-900'
            }
          >
            {t(key)}
          </li>
        ))}
        {frame.typing && (
          <li
            className="flex gap-1 self-start rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2.5"
            aria-label={t('dashboard.bealeTextingCard.demo.typingLabel')}
            data-testid="beale-chat-typing"
          >
            {[0, 150, 300].map((delay) => (
              <span
                key={delay}
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"
                style={{ animationDelay: `${delay}ms` }}
              />
            ))}
          </li>
        )}
      </ol>
    </div>
  );
}
